import { Component } from "react";

function isStaleBundleError(message) {
  return /useHoldings is not defined|verifySession is not defined|is not a <Route> component|Loading chunk|ChunkLoadError|dynamically imported module/i.test(message);
}

async function clearStaleAppAndReload() {
  const recoveryKey = "investing-os-cache-recovery:route-boundary";
  if (sessionStorage.getItem(recoveryKey)) {
    window.location.reload();
    return;
  }
  sessionStorage.setItem(recoveryKey, "1");

  if ("caches" in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }

  const registration = "serviceWorker" in navigator
    ? await navigator.serviceWorker.getRegistration()
    : null;
  if (registration) await registration.unregister();
  window.location.reload();
}

export default class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    console.error("[RouteErrorBoundary]", error);
    if (isStaleBundleError(String(error?.message || error || ""))) {
      clearStaleAppAndReload().catch(() => window.location.reload());
    }
  }

  handleRetry = () => {
    this.setState({ error: null });
    const message = String(this.state.error?.message || this.state.error || "");
    if (isStaleBundleError(message)) {
      clearStaleAppAndReload().catch(() => window.location.reload());
      return;
    }
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const message = String(error?.message || error || "Unknown error");
    const isChunkError = /Loading chunk|ChunkLoadError|dynamically imported module/i.test(message);

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-xl font-semibold text-foreground">
            {isChunkError ? "Update required" : "Something went wrong"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isChunkError
              ? "A new version of the app is available, or a page bundle failed to load. Refresh to continue."
              : message}
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            Refresh app
          </button>
        </div>
      </div>
    );
  }
}
